import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { AddOnItemName, SubscriptionPortalFlow } from "schemas/dashboard";

import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import { getFollowUpAssistantDate } from "../PlanCard/helper";

interface CancelDialogProps {
  open: boolean;
  onClose: () => void;
  warningText?: string;
}

export default function CancelDialog({
  open,
  onClose,
  warningText,
}: CancelDialogProps) {
  const { subscription, plan } = useContext(SubscriptionContext);
  const { checkoutLoading, handleManagePlan } = useSubscription();

  const isPartnershipData = plan === AddOnItemName.partnership_data;
  const isCustomEmailData = plan === AddOnItemName.custom_email;

  const daysAway = getFollowUpAssistantDate(subscription);

  const endDate =
    isPartnershipData && subscription?.partnershipData?.billingCycle
      ? subscription?.partnershipData?.billingCycle[1]
      : isCustomEmailData && subscription?.customEmailData?.billingCycle
        ? subscription.customEmailData.billingCycle[1]
        : subscription?.billingCycle[1];

  const handleContinueToCancel = async () => {
    if (!plan) return;
    handleManagePlan(plan, SubscriptionPortalFlow.subscription_cancel);
  };

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose}>
      <IconButton
        sx={{
          color: "primary.main",
          fontSize: 14,
          position: "absolute",
          right: 10,
          top: 10,
        }}
        onClick={onClose}
      >
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <DialogTitle>Before You Go</DialogTitle>

      <DialogContent>
        {warningText ? (
          <Typography gutterBottom>
            <strong>Warning: </strong>
            {warningText}
          </Typography>
        ) : daysAway !== undefined ? (
          <>
            <Typography gutterBottom>
              <strong>Warning: </strong> Before you go - your automated
              check-ins are set to run{" "}
              <Box component="span" sx={{ color: blue[600] }}>
                <strong>
                  {daysAway === "today"
                    ? "today"
                    : `in ${daysAway} day${daysAway !== 1 ? "s" : ""}`}
                </strong>
              </Box>
              .
            </Typography>
            <Typography paragraph>
              If you unsubscribe now, they'll be canceled. Would you like to
              keep your subscription active until after your check-ins run?
            </Typography>
          </>
        ) : (
          <Typography gutterBottom>
            <strong>Warning: </strong> All follow-ups and brand requests beyond
            your cancellation date will be deleted.
          </Typography>
        )}

        <Box display="flex" gap={1} justifyContent="center" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              trackEvent("User Feedback - Keep Subscription Button Clicked");
              onClose();
            }}
            disableElevation
          >
            Keep Subscription
          </Button>
          <LoadingButton
            loading={checkoutLoading === plan}
            color="secondary"
            onClick={handleContinueToCancel}
          >
            Continue to Cancel
          </LoadingButton>
        </Box>
        {endDate && (
          <Typography paragraph color="textSecondary" variant="caption">
            Your subscription will remain active until the end of your current
            billing period on{" "}
            <strong>
              {formatEpochTimestamp({
                epochTimestamp: endDate,
                addYear: true,
              })}
            </strong>
            .
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
